import { Alert, Button, Card, Form, Input, Typography, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

const { Title, Paragraph } = Typography;

type ApiError = { message?: string; param?: string; code?: string };
type ApiResponse = {
  status?: number;
  errors?: ApiError[];
  meta?: { is_authenticated?: boolean };
};

const BASE = "/_allauth/browser/v1/auth/password/reset";

function getCsrfToken(): string | null {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "csrftoken") return value;
  }
  return null;
}

async function callResetApi(
  method: "GET" | "POST",
  key: string,
  body?: Record<string, unknown>,
): Promise<ApiResponse> {
  const headers: HeadersInit = {
    Accept: "application/json",
    "X-Password-Reset-Key": key,
  };
  if (method !== "GET") {
    headers["Content-Type"] = "application/json";
    const csrf = getCsrfToken();
    if (csrf) headers["X-CSRFToken"] = csrf;
  }

  const res = await fetch(BASE, {
    method,
    credentials: "include",
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  try {
    return (await res.json()) as ApiResponse;
  } catch {
    return { status: res.status };
  }
}

export const ResetPasswordKey = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetKey = key || "";

  useEffect(() => {
    let mounted = true;
    const boot = async () => {
      if (!resetKey) {
        setError("Missing reset key.");
        setLoading(false);
        return;
      }
      try {
        const payload = await callResetApi("GET", resetKey);
        if (!mounted) return;
        if (payload.status === 200) {
          setReady(true);
        } else {
          setError(payload.errors?.[0]?.message || "Reset link is invalid or expired.");
        }
      } catch {
        if (!mounted) return;
        setError("Could not validate reset link.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    boot();
    return () => {
      mounted = false;
    };
  }, [resetKey]);

  const onFinish = async (values: { password: string; password2: string }) => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = await callResetApi("POST", resetKey, {
        key: resetKey,
        password: values.password,
      });

      if (payload.status === 200 || (payload.status === 401 && Boolean(payload.meta?.is_authenticated))) {
        message.success("Password changed successfully.");
        navigate("/", { replace: true });
        return;
      }

      setError(payload.errors?.[0]?.message || "Password reset failed.");
    } catch {
      setError("Network error while resetting password.");
    } finally {
      setSubmitting(false);
    }
  };

  const cardTitle = useMemo(() => {
    if (loading) return "Checking reset link";
    return "Set new password";
  }, [loading]);

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 16 }}>
      <Card style={{ width: "100%", maxWidth: 460 }}>
        <Title level={4} style={{ marginTop: 0 }}>{cardTitle}</Title>
        {loading ? <Paragraph>Please wait...</Paragraph> : null}

        {!loading && error ? (
          <Alert type="error" showIcon message={error} />
        ) : null}

        {!loading && ready ? (
          <Form layout="vertical" onFinish={onFinish}>
            <Form.Item
              label="New password"
              name="password"
              rules={[{ required: true, message: "Please enter password" }, { min: 8 }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              label="Repeat password"
              name="password2"
              dependencies={["password"]}
              rules={[
                { required: true, message: "Please repeat password" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords do not match"));
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting} block>
              Reset password
            </Button>
          </Form>
        ) : null}
      </Card>
    </div>
  );
};
